import { Collapse, Form, Table } from 'antd';
import { forwardRef, useImperativeHandle, useState } from 'react';

import TableComponent from 'components/Table/TableComponent';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import { formatCurrency, total } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';

const { CaretRightOutlined } = icons;

interface IProps {
  plan: any;
  data: any;
  loading?: boolean;
}

const months = [
  'janSaleamt',
  'febSaleamt',
  'marSaleamt',
  'aprSaleamt',
  'maySaleamt',
  'junSaleamt',
  'julSaleamt',
  'augSaleamt',
  'sepSaleamt',
  'octSaleamt',
  'novSaleamt',
  'decSaleamt',
];

const TableHOPLanDetail = forwardRef<any, IProps>(
  ({ plan, data, loading }, ref) => {
    const columns: ColumnsTypeCustom = [
      {
        fixed: 'left',
        title: 'Chi nhánh',
        dataIndex: 'tplant',
        width: 220,
      },
      {
        title: 'Loại doanh thu',
        dataIndex: ['ttypeRevenue', 'name'],
        width: 150,
        hidden: true,
      },
      {
        title: 'Tháng 1',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'janSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 2',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'febSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 3',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'marSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 4',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'aprSaleamt',
        width: 150,
        render: (value) => value,
      },
      {
        title: ' Tháng 5',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'maySaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 6',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'junSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 7',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'julSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 8',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'augSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 9',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'sepSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 10',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'octSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 11',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'novSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: 'Tháng 12',
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'decSaleamt',
        width: 150,
        render: (value) =>
          formatCurrency({
            value,
            round: true,
          }),
      },
      {
        title: `${Number(plan.year)}`,
        align: 'right',
        type: 'number',
        currency: 'vi-VN',
        dataIndex: 'currentYear',
        width: 150,
        render(_, record) {
          const { totalOfAll } = total(record);

          return formatCurrency({
            value: totalOfAll,
            round: true,
          });
        },
      },
    ];

    const getData = () => {
      const summary = total(data);
      const updatedData = data.map((record: any) => ({
        ...record,
        currency: 'Triệu VND',
        currentYear: total(record).totalOfAll,
      }));

      updatedData.push({
        tplant: 'Tổng',
        ...summary.monthlyTotals,
        currentYear: summary.totalOfAll,
        currency: 'Triệu VND',
      });

      return { headerRow: columns, datas: updatedData };
    };

    useImperativeHandle(ref, () => ({
      getData,
    }));

    const renderCollapseTable = (
      title: string,
      key: string,
      typeRevenue: string,
    ) => {
      const filteredData = data?.filter(
        (item: any) => item.typeRevenue === typeRevenue,
      );

      return (
        <Content key={key}>
          <Collapse
            defaultActiveKey={['1']}
            ghost
            size="small"
            expandIcon={({ isActive }) => (
              <CaretRightOutlined
                style={{
                  fontSize: '20px',
                  color: isActive ? '#0077cc' : '#003056',
                }}
                rotate={isActive ? -90 : 0}
              />
            )}
            items={[
              {
                key: key,
                label: (
                  <p className="flex items-center justify-between">
                    <span className="font-bold text-[16px]">{title}</span>
                    <span>Đơn vị: {plan?.unitMoney || 'triệu VND'}</span>
                  </p>
                ),
                children: (
                  <TableComponent
                    rowKey={(record) => record.id}
                    dataSource={filteredData}
                    columns={columns}
                    loading={loading}
                    pagination={false}
                    summary={(pageData) => {
                      const { monthlyTotals, totalOfAll } = total(pageData);

                      if (!filteredData?.length) return null;

                      return (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            Tổng
                          </Table.Summary.Cell>
                          {Object.values(monthlyTotals).map((value, index) => (
                            <Table.Summary.Cell
                              key={index}
                              index={index + 1}
                              className="text-right"
                            >
                              {formatCurrency({
                                value: Number(value),
                                round: true,
                              })}
                            </Table.Summary.Cell>
                          ))}
                          <Table.Summary.Cell index={1} className="text-right">
                            {formatCurrency({
                              value: totalOfAll,
                              round: true,
                            })}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      );
                    }}
                  />
                ),
              },
            ]}
          />
        </Content>
      );
    };

    return (
      <>
        {renderCollapseTable('Doanh thu hóa đơn', '1', 'DT01')}
        {renderCollapseTable('Doanh thu công', '2', 'DT02')}
        {renderCollapseTable('Lãi gộp', '3', 'DT03')}
        {renderCollapseTable('Lợi nhuận trước thuế', '4', 'DT04')}
      </>
    );
  },
);

export default TableHOPLanDetail;
